<template>
  <div class="container-fluid mt-2 monitor text-left">
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
      <empresa-combo :selecionar="changeEmpresa" :noStyle="false" />
      <div class="d-flex align-items-center">
        <span class="atualizado mr-3">
          <span class="ao-vivo" :class="{ pausado }"></span>
          {{ pausado ? "pausado" : ultimaAtualizacao ? `ao vivo · ${ultimaAtualizacao}` : "conectando..." }}
        </span>
        <button class="btn btn-sm btn-outline-secondary mr-2" @click="pausado = !pausado">
          <i :class="pausado ? 'fa fa-play' : 'fa fa-pause'"></i> {{ pausado ? "Retomar" : "Pausar" }}
        </button>
        <router-link class="btn btn-sm btn-outline-primary" to="/configuracoes/impressoras">
          <i class="fa fa-cog"></i> Impressoras
        </router-link>
      </div>
    </div>

    <b-alert v-if="dados && (!dados.recursos.registradoEm || !dados.recursos.monitor)" show variant="warning" class="small">
      Atraso exato de cada impressão e histórico de travadas/quedas de conexão ainda não estão
      ativos neste banco. Rode as migrations da API (<code>node ace migration:run</code>) para ativar.
    </b-alert>
    <b-alert v-if="erro" show variant="danger" class="small">{{ erro }}</b-alert>

    <div v-if="!dados" class="text-center my-5 text-muted">
      <b-spinner small type="grow" variant="primary" /> carregando monitor...
    </div>

    <div v-else>
      <!-- 1. numeros do momento -->
      <div class="kpis">
        <div class="kpi">
          <div class="kpi-label">Computadores funcionando</div>
          <div class="kpi-valor">
            {{ resumo.online }}<span class="kpi-de"> de {{ resumo.ativos }}</span>
          </div>
          <div class="kpi-nota" v-if="resumo.parados">
            <i class="fa fa-exclamation-circle st-critical"></i> {{ resumo.parados }} travado(s) agora
          </div>
          <div class="kpi-nota" v-else-if="resumo.online === resumo.ativos && resumo.ativos">
            <i class="fa fa-check-circle st-good"></i> todos respondendo
          </div>
        </div>
        <div class="kpi">
          <div class="kpi-label">Na fila agora</div>
          <div class="kpi-valor">{{ resumo.naFila }}</div>
          <div class="kpi-nota" v-if="resumo.naFila">
            <i class="fa fa-hourglass-half st-warning"></i> mais antigo há {{ duracao(resumo.maiorEspera) }}
          </div>
          <div class="kpi-nota" v-else><i class="fa fa-check-circle st-good"></i> tudo impresso</div>
        </div>
        <div class="kpi" v-if="dados.recursos.registradoEm">
          <div class="kpi-label">Atraso médio hoje</div>
          <div class="kpi-valor">{{ resumo.mediaHoje === null ? "—" : duracao(resumo.mediaHoje) }}</div>
          <div class="kpi-nota" v-if="resumo.maxHoje !== null">
            <i :class="iconeAtraso(resumo.maxHoje)"></i> maior: {{ duracao(resumo.maxHoje) }}
          </div>
        </div>
        <div class="kpi" v-if="dados.recursos.monitor">
          <div class="kpi-label">Travadas hoje</div>
          <div class="kpi-valor">{{ resumo.travadas }}</div>
          <div class="kpi-nota" v-if="resumo.quedas">
            <i class="fa fa-plug st-serious"></i> {{ resumo.quedas }} queda(s) de conexão
          </div>
          <div class="kpi-nota" v-else><i class="fa fa-check-circle st-good"></i> nenhuma queda de conexão</div>
        </div>
      </div>

      <!-- 2. um cartao por computador -->
      <div class="pcs">
        <div v-for="pc in pcsAtivos" :key="pc.computador" class="pc" :class="pc.situacao">
          <div class="pc-topo">
            <div class="semaforo" :class="pc.situacao">
              <i :class="iconeSituacao(pc.situacao)"></i>
            </div>
            <div class="flex-grow-1">
              <div class="pc-nome">{{ pc.computador }}</div>
              <div class="pc-situacao">{{ textoSituacao(pc) }}</div>
            </div>
          </div>
          <div v-if="pc.conexoes_abertas > 1" class="pc-alerta">
            <i class="fa fa-plug"></i>
            {{ pc.conexoes_abertas - 1 }} conexão(ões) caída(s) sem fechar — sinal de rede instável neste computador
          </div>
          <div class="impressoras">
            <div v-for="imp in pc.impressoras.filter((i) => i.ativo)" :key="imp.cod_impressora" class="imp">
              <i class="fa fa-print imp-icone"></i>
              <div class="flex-grow-1">
                <div class="imp-nome">{{ imp.nome }}</div>
                <div class="imp-ultima">{{ ultimaDaImpressora(imp) }}</div>
              </div>
              <div v-if="imp.pendentes" class="fila-badge" :class="imp.espera_s > 10 ? 'critical' : 'warning'">
                <i class="fa fa-hourglass-half"></i> {{ imp.pendentes }} na fila
              </div>
              <div v-else class="fila-badge good"><i class="fa fa-check"></i> em dia</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="pcsDesligados.length" class="desligados">
        <i class="fa fa-power-off mr-1"></i>
        Sem conexão: <b>{{ pcsDesligados.map((p) => p.computador).join(", ") }}</b>
        <span v-if="pendentesDesligados">
          — {{ pendentesDesligados }} item(ns) aguardando nesses computadores. Se forem computadores antigos,
          desative as impressoras deles em <router-link to="/configuracoes/impressoras">Impressoras</router-link>.
        </span>
      </div>

      <!-- 3. linha do tempo -->
      <div class="bloco">
        <div class="bloco-topo">
          <div>
            <div class="bloco-titulo">Últimas {{ dados.janelaHoras }} horas</div>
            <div class="bloco-sub">
              Cada ponto é um pedido/senha impresso; quanto mais alto, mais demorou para sair.
            </div>
          </div>
          <div class="legenda">
            <span><span class="ponto good"></span> até 10s</span>
            <span><span class="ponto warning"></span> 10 a 30s</span>
            <span><span class="ponto critical"></span> mais de 30s</span>
            <span v-if="temSemMedicao"><span class="ponto"></span> sem medição</span>
            <span><span class="bloco-travou"></span> travado</span>
            <span><span class="marca-queda"></span> queda de conexão</span>
          </div>
        </div>

        <div v-for="pista in pistas" :key="pista.computador" class="pista">
          <div class="pista-nome">
            <i :class="iconeSituacao(pista.situacao)" class="mr-1"></i>{{ pista.computador }}
            <div class="pista-resumo">{{ pista.pontos.length }} impressões</div>
          </div>
          <div class="pista-area">
            <div class="guia" style="bottom: 16.67%"><span>10s</span></div>
            <div class="guia" style="bottom: 50%"><span>30s</span></div>
            <div class="guia" style="bottom: 100%"><span>60s+</span></div>
            <div
              v-for="(t, i) in pista.travadas"
              :key="'t' + i"
              class="travou"
              :style="{ left: t.left + '%', width: Math.max(t.width, 0.4) + '%' }"
              v-b-tooltip.hover
              :title="t.texto"
            >
              <span v-if="t.width > 7">{{ t.rotulo }}</span>
            </div>
            <div
              v-for="(q, i) in pista.quedas"
              :key="'q' + i"
              class="queda"
              :style="{ left: q.left + '%' }"
              v-b-tooltip.hover
              :title="q.texto"
            >
              <i class="fa fa-plug"></i>
            </div>
            <div
              v-for="p in pista.pontos"
              :key="p.cod_impressao"
              class="alvo"
              :style="{ left: p.left + '%', bottom: p.bottom + '%' }"
              v-b-tooltip.hover
              :title="p.texto"
            >
              <span class="ponto" :class="p.nivel"></span>
            </div>
          </div>
        </div>
        <div v-if="!pistas.length" class="text-muted small py-3">Nenhum computador ativo no período.</div>

        <div class="eixo">
          <div class="eixo-area">
            <span v-for="t in marcasTempo" :key="t.rotulo" :style="{ left: t.left + '%' }">{{ t.rotulo }}</span>
            <span class="agora" style="left: 100%">agora</span>
          </div>
        </div>
      </div>

      <!-- 4. fila recente e ocorrencias -->
      <div class="row">
        <div class="col-12" :class="{ 'col-xl-8': dados.recursos.monitor }">
          <div class="bloco">
            <div class="bloco-titulo mb-2">Fila recente</div>
            <div v-for="f in filaAgrupada" :key="f.chave" class="fila-item">
              <span class="fila-hora">{{ hora(f.entrou_fila) }}</span>
              <span class="fila-o-que">
                <i :class="iconeTipo(f.tipos[f.tipos.length - 1])" class="mr-1"></i>{{ f.tipos.map(nomeTipo).join(" + ") }}
                <b v-if="f.senha">#{{ f.senha }}</b>
                <small class="text-muted"> · caixa {{ f.cod_caixa }}</small>
              </span>
              <span class="fila-imps">
                <span v-for="i in f.impressoes" :key="i.cod_impressora" class="chip" :class="classeImpressao(f, i)">
                  <i :class="iconeImpressao(f, i)"></i> {{ i.impressora }} · {{ textoImpressao(f, i) }}
                </span>
              </span>
            </div>
            <div v-if="!dados.fila.length" class="text-muted small">Nada na fila no período.</div>
          </div>
        </div>
        <div class="col-12 col-xl-4" v-if="dados.recursos.monitor">
          <div class="bloco">
            <div class="bloco-titulo mb-2">Ocorrências de hoje</div>
            <div v-for="e in dados.eventos.filter((x) => x.evento !== 'PAROU')" :key="e.id" class="ocorrencia">
              <span class="ocorrencia-icone" :class="e.evento === 'RECONECTOU' ? 'serious' : 'critical'">
                <i :class="e.evento === 'RECONECTOU' ? 'fa fa-plug' : 'fa fa-pause-circle'"></i>
              </span>
              <div class="flex-grow-1">
                <div>
                  <b>{{ e.computador }}</b>
                  {{ e.evento === "RECONECTOU" ? "perdeu a conexão e reconectou" : `ficou ${duracao(e.segundos)} sem imprimir` }}
                </div>
                <small class="text-muted">
                  {{ hora(e.evento === "VOLTOU" ? e.inicio : e.registrado_em) }}
                  <template v-if="e.evento === 'VOLTOU'"> · {{ e.comando === "Query" ? "banco demorou" : "o computador parou de consultar" }}</template>
                </small>
              </div>
            </div>
            <div v-if="!dados.eventos.some((x) => x.evento !== 'PAROU')" class="text-muted small">
              <i class="fa fa-check-circle st-good"></i> Nenhuma travada ou queda hoje.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmpresaCombo from "../../components/empresa/EmpresaCombo.vue";
import ImpressorasConfigLib from "@/libs/ImpressorasConfigLib";

const INTERVALO_MS = 3000;
const ORDEM_SITUACAO = { parado: 0, online: 1, desconhecido: 2, offline: 3 };
// Escala vertical dos pontos: 0 a 60s; acima disso o ponto fica no topo.
const ATRASO_TOPO_S = 60;
const TIPOS = {
  senha: { nome: "Senha", icone: "fa fa-ticket-alt" },
  pedido: { nome: "Pedido", icone: "fa fa-receipt" },
  mesas_pedido: { nome: "Pedido mesa", icone: "fa fa-utensils" },
  mesa_pedido: { nome: "Pedido mesa", icone: "fa fa-utensils" },
  mesas_item: { nome: "Item mesa", icone: "fa fa-utensils" },
  controle_entrega: { nome: "Controle entrega", icone: "fa fa-motorcycle" },
  conta_mesa: { nome: "Conta mesa", icone: "fa fa-file-invoice-dollar" },
  conta_comanda: { nome: "Conta comanda", icone: "fa fa-file-invoice-dollar" },
  caixa_fechamento: { nome: "Fechamento", icone: "fa fa-cash-register" },
  caixa_fita: { nome: "Fita de caixa", icone: "fa fa-cash-register" },
  confirmacao_saida: { nome: "Confirm. saída", icone: "fa fa-check-double" },
};

export default {
  components: { EmpresaCombo },
  data() {
    return {
      empresa: null,
      dados: null,
      erro: null,
      carregando: false,
      pausado: false,
      ultimaAtualizacao: null,
      timer: null,
    };
  },
  computed: {
    pcsOrdenados() {
      return [...this.dados.pcs].sort(
        (a, b) =>
          ORDEM_SITUACAO[a.situacao] - ORDEM_SITUACAO[b.situacao] || a.computador.localeCompare(b.computador)
      );
    },
    // Computadores com impressora ativa que estao conectados (ou travados).
    pcsAtivos() {
      return this.pcsOrdenados.filter((p) => p.situacao !== "offline" && p.impressoras.some((i) => i.ativo));
    },
    pcsDesligados() {
      return this.pcsOrdenados.filter((p) => p.situacao === "offline" && p.impressoras.some((i) => i.ativo));
    },
    pendentesDesligados() {
      return this.pcsDesligados.reduce((s, p) => s + p.impressoras.reduce((t, i) => t + (i.ativo ? i.pendentes : 0), 0), 0);
    },
    resumo() {
      const ativos = this.pcsAtivos.length + this.pcsDesligados.length;
      const naFila = this.pcsAtivos.reduce((s, p) => s + p.impressoras.reduce((t, i) => t + (i.ativo ? i.pendentes : 0), 0), 0);
      const maiorEspera = Math.max(0, ...this.pcsAtivos.flatMap((p) => p.impressoras.map((i) => (i.pendentes ? i.espera_s : 0))));
      const comAtraso = this.dados.pcs.filter((p) => p.atraso_hoje);
      const total = comAtraso.reduce((s, p) => s + Number(p.atraso_hoje.impressoes), 0);
      return {
        ativos,
        online: this.pcsAtivos.filter((p) => p.situacao === "online").length,
        parados: this.pcsAtivos.filter((p) => p.situacao === "parado").length,
        naFila,
        maiorEspera,
        mediaHoje: total
          ? comAtraso.reduce((s, p) => s + Number(p.atraso_hoje.media_s) * Number(p.atraso_hoje.impressoes), 0) / total
          : null,
        maxHoje: comAtraso.length ? Math.max(...comAtraso.map((p) => Number(p.atraso_hoje.max_s))) : null,
        travadas: this.dados.pcs.reduce((s, p) => s + p.paradas_hoje, 0),
        quedas: this.dados.pcs.reduce((s, p) => s + p.reconexoes_hoje, 0),
      };
    },
    temSemMedicao() {
      return this.dados.impressoes.some((i) => i.atraso_s === null || i.atraso_s === undefined);
    },
    // Senha + pedido do mesmo numero viram uma linha; impressoras de PC desligado ficam de fora
    // (ja aparecem resumidas em "Sem conexao"). Por impressora vale a via que mais demorou.
    filaAgrupada() {
      const grupos = [];
      for (const f of this.dados.fila) {
        const chave = `${f.codigo}|${this.hora(f.entrou_fila)}|${f.cod_caixa}`;
        let g = grupos.find((x) => x.chave === chave);
        if (!g) {
          if (grupos.length >= 15) continue;
          g = { ...f, chave, tipos: [], impressoes: [] };
          grupos.push(g);
        }
        if (!g.tipos.includes(f.tipo)) {
          // senha antes do pedido
          if (f.tipo === "senha") g.tipos.unshift(f.tipo);
          else g.tipos.push(f.tipo);
        }
        for (const i of f.impressoes) {
          if (i.pendente && i.pc_situacao === "offline") continue;
          const atual = g.impressoes.find((x) => x.cod_impressora === i.cod_impressora);
          if (!atual) g.impressoes.push({ ...i });
          else if (i.pendente || Number(i.atraso_s) > Number(atual.atraso_s)) Object.assign(atual, i);
        }
      }
      return grupos;
    },
    janela() {
      const fim = this.$moment(this.dados.agora).valueOf();
      return { inicio: fim - this.dados.janelaHoras * 3600000, fim };
    },
    marcasTempo() {
      const { inicio, fim } = this.janela;
      const passo = 30 * 60000;
      const marcas = [];
      for (let t = Math.ceil(inicio / passo) * passo; t < fim - passo / 3; t += passo) {
        marcas.push({ left: this.posicao(t), rotulo: this.$moment(t).format("HH:mm") });
      }
      // Nao encosta no rotulo "agora".
      return marcas.filter((m) => m.left <= 88);
    },
    // Uma pista por computador: pontos de impressao, travadas e quedas no mesmo eixo de tempo.
    pistas() {
      const pcs = [...this.pcsAtivos, ...this.pcsDesligados];
      const comImpressao = new Set(this.dados.impressoes.map((i) => i.computador));
      return pcs
        .filter((pc) => pc.situacao !== "offline" || comImpressao.has(pc.computador))
        .map((pc) => ({
          computador: pc.computador,
          situacao: pc.situacao,
          pontos: this.pontosDoPc(pc.computador),
          travadas: this.travadasDoPc(pc),
          quedas: this.dados.eventos
            .filter((e) => e.computador === pc.computador && e.evento === "RECONECTOU" && this.naJanela(e.registrado_em))
            .map((e) => ({
              left: this.posicao(e.registrado_em),
              texto: `${this.hora(e.registrado_em)} — perdeu a conexão e abriu outra`,
            })),
        }));
    },
  },
  mounted() {
    this.timer = setInterval(this.atualizar, INTERVALO_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    changeEmpresa(emp) {
      this.empresa = emp;
      this.dados = null;
      this.atualizar();
    },
    async atualizar() {
      if (!this.empresa || this.carregando || this.pausado || document.hidden) return;
      this.carregando = true;
      try {
        const res = await ImpressorasConfigLib.monitor(this.empresa.cod_empresa);
        if (res.success) {
          this.dados = res.data;
          this.erro = null;
          this.ultimaAtualizacao = this.$moment().format("HH:mm:ss");
        } else {
          this.erro = res.message;
        }
      } catch (e) {
        this.erro = "Não foi possível atualizar o monitor (sem conexão com a API?)";
      }
      this.carregando = false;
    },
    posicao(data) {
      const { inicio, fim } = this.janela;
      const t = this.$moment(data).valueOf();
      return Math.min(100, Math.max(0, ((t - inicio) / (fim - inicio)) * 100));
    },
    naJanela(data) {
      return this.$moment(data).valueOf() >= this.janela.inicio;
    },
    nivel(s) {
      if (s === null || s === undefined) return "sem";
      return s > 30 ? "critical" : s > 10 ? "warning" : "good";
    },
    // Um ponto por item da fila no computador: o atraso e o da impressora que mais demorou.
    pontosDoPc(computador) {
      const porItem = {};
      for (const i of this.dados.impressoes) {
        if (i.computador !== computador) continue;
        const item = porItem[i.cod_impressao] || (porItem[i.cod_impressao] = { ...i, atrasos: [] });
        if (i.atraso_s !== null && i.atraso_s !== undefined) item.atrasos.push(`${i.impressora}: ${this.duracao(i.atraso_s)}`);
        const atraso = i.atraso_s === null || i.atraso_s === undefined ? null : Number(i.atraso_s);
        if (atraso !== null && (item.maior === undefined || atraso > item.maior)) item.maior = atraso;
      }
      return Object.values(porItem).map((i) => {
        const atraso = i.maior === undefined ? null : i.maior;
        const nivel = this.nivel(atraso);
        const rotuloNivel = { good: "normal", warning: "lento", critical: "muito lento", sem: "" }[nivel];
        return {
          cod_impressao: i.cod_impressao,
          left: this.posicao(i.entrou_fila),
          bottom: atraso === null ? 0 : (Math.min(atraso, ATRASO_TOPO_S) / ATRASO_TOPO_S) * 100,
          nivel,
          texto:
            `${this.hora(i.entrou_fila)} — ${this.nomeTipo(i.tipo)}${i.senha ? " #" + i.senha : ""}` +
            (atraso === null ? "" : ` · saiu em ${this.duracao(atraso)} (${rotuloNivel})`) +
            (i.atrasos.length > 1 ? ` · ${i.atrasos.join(", ")}` : ""),
        };
      });
    },
    travadasDoPc(pc) {
      const lista = this.dados.eventos
        .filter((e) => e.computador === pc.computador && e.evento === "VOLTOU" && e.inicio)
        .map((e) => ({ inicio: this.$moment(e.inicio).valueOf(), segundos: Number(e.segundos) }));
      if (pc.situacao === "parado") {
        lista.push({ inicio: this.janela.fim - pc.ocioso_s * 1000, segundos: pc.ocioso_s, agora: true });
      }
      return lista
        .filter((t) => t.inicio + t.segundos * 1000 >= this.janela.inicio)
        .map((t) => {
          const left = this.posicao(t.inicio);
          const width = this.posicao(t.inicio + t.segundos * 1000) - left;
          return {
            left,
            width,
            rotulo: `travou ${this.duracao(t.segundos)}`,
            texto: `${this.$moment(t.inicio).format("HH:mm:ss")} — ${t.agora ? "travado há" : "ficou"} ${this.duracao(t.segundos)} sem consultar a fila`,
          };
        });
    },
    ultimaDaImpressora(imp) {
      const datas = this.dados.impressoes
        .filter((i) => i.impressora === imp.nome && i.impresso_em)
        .map((i) => this.$moment(i.impresso_em).valueOf());
      if (!datas.length) return `sem impressão nas últimas ${this.dados.janelaHoras}h`;
      return `última ${this.$moment(Math.max(...datas)).from(this.$moment(this.dados.agora))}`;
    },
    hora(data) {
      return data ? this.$moment(data).format("HH:mm:ss") : "";
    },
    // Segundos desde "data", pelo relogio do banco (nao depende do relogio deste computador).
    idade(data) {
      return this.$moment(this.dados.agora).diff(this.$moment(data), "seconds");
    },
    duracao(s) {
      s = Number(s) || 0;
      if (s < 10) return `${Math.round(s * 10) / 10}s`.replace(".", ",");
      s = Math.round(s);
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
      if (m < 60) return `${m}min ${s % 60}s`;
      return `${Math.floor(m / 60)}h ${m % 60}min`;
    },
    nomeTipo(tipo) {
      return (TIPOS[tipo] && TIPOS[tipo].nome) || tipo;
    },
    iconeTipo(tipo) {
      return (TIPOS[tipo] && TIPOS[tipo].icone) || "fa fa-print";
    },
    iconeSituacao(situacao) {
      return {
        online: "fa fa-check st-good",
        parado: "fa fa-pause st-critical",
        offline: "fa fa-power-off st-muted",
      }[situacao] || "fa fa-question st-muted";
    },
    iconeAtraso(s) {
      return { good: "fa fa-check-circle st-good", warning: "fa fa-exclamation-triangle st-warning", critical: "fa fa-times-circle st-critical" }[this.nivel(s)];
    },
    textoSituacao(pc) {
      if (pc.situacao === "online") return "Funcionando · consultou a fila há " + pc.ocioso_s + "s";
      if (pc.situacao === "parado") return `Travado · sem consultar a fila há ${this.duracao(pc.ocioso_s)}`;
      if (pc.situacao === "offline") return "Desligado ou app de impressão fechado";
      return "Situação desconhecida";
    },
    textoImpressao(f, i) {
      if (i.pendente) {
        return i.pc_situacao === "offline" ? "PC desligado" : `aguardando ${this.duracao(this.idade(f.entrou_fila))}`;
      }
      return i.atraso_s !== null && i.atraso_s !== undefined ? this.duracao(i.atraso_s) : "ok";
    },
    classeImpressao(f, i) {
      if (i.pendente) return i.pc_situacao === "offline" ? "cinza" : this.idade(f.entrou_fila) > 10 ? "critical" : "warning";
      const n = this.nivel(i.atraso_s === null || i.atraso_s === undefined ? 0 : Number(i.atraso_s));
      return n;
    },
    iconeImpressao(f, i) {
      if (i.pendente) return i.pc_situacao === "offline" ? "fa fa-power-off" : "fa fa-hourglass-half";
      const n = this.nivel(i.atraso_s === null || i.atraso_s === undefined ? 0 : Number(i.atraso_s));
      return { good: "fa fa-check", warning: "fa fa-exclamation-triangle", critical: "fa fa-times" }[n];
    },
  },
};
</script>

<style lang="scss" scoped>
// Cores de status (good/warning/serious/critical): sempre acompanhadas de icone + texto.
$good: #0ca30c;
$warning: #fab219;
$serious: #ec835a;
$critical: #d03b3b;
$superficie: #fcfcfb;
$tinta: #0b0b0b;
$tinta-2: #52514e;
$tinta-3: #898781;
$grade: #e1e0d9;
$borda: rgba(11, 11, 11, 0.1);

.monitor {
  color: $tinta;
  font-size: 14px;
}
.st-good { color: $good; }
.st-warning { color: darken($warning, 10%); }
.st-serious { color: $serious; }
.st-critical { color: $critical; }
.st-muted { color: $tinta-3; }

.atualizado {
  font-size: 12px;
  color: $tinta-2;
  .ao-vivo {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $good;
    margin-right: 4px;
    animation: pulso 1.6s infinite;
    &.pausado {
      background: $tinta-3;
      animation: none;
    }
  }
}
@keyframes pulso {
  0% { box-shadow: 0 0 0 0 rgba(12, 163, 12, 0.5); }
  70% { box-shadow: 0 0 0 7px rgba(12, 163, 12, 0); }
  100% { box-shadow: 0 0 0 0 rgba(12, 163, 12, 0); }
}

// 1. numeros do momento
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
.kpi {
  background: $superficie;
  border: 1px solid $borda;
  border-radius: 8px;
  padding: 12px 14px;
  .kpi-label {
    color: $tinta-2;
    font-size: 12px;
  }
  .kpi-valor {
    font-size: 30px;
    font-weight: 600;
    line-height: 1.2;
  }
  .kpi-de {
    font-size: 16px;
    font-weight: normal;
    color: $tinta-2;
  }
  .kpi-nota {
    font-size: 12px;
    color: $tinta-2;
  }
}

// 2. computadores
.pcs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}
.pc {
  background: $superficie;
  border: 1px solid $borda;
  border-radius: 8px;
  padding: 12px 14px;
  &.parado {
    border-color: $critical;
    box-shadow: 0 0 0 1px $critical inset;
  }
}
.pc-topo {
  display: flex;
  align-items: center;
  gap: 12px;
}
.semaforo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  flex-shrink: 0;
  background: $tinta-3;
  i {
    color: white;
  }
  &.online {
    background: $good;
    animation: pulso 2s infinite;
  }
  &.parado {
    background: $critical;
  }
}
.pc-nome {
  font-weight: 600;
  font-size: 15px;
}
.pc-situacao {
  font-size: 12px;
  color: $tinta-2;
}
.pc.parado .pc-situacao {
  color: $critical;
  font-weight: 600;
}
.pc-alerta {
  margin-top: 8px;
  font-size: 12px;
  background: rgba(236, 131, 90, 0.12);
  border-radius: 4px;
  padding: 4px 8px;
  i {
    color: $serious;
  }
}
.impressoras {
  margin-top: 10px;
}
.imp {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-top: 1px solid $grade;
  .imp-icone {
    font-size: 18px;
    color: $tinta-2;
  }
  .imp-nome {
    font-weight: 500;
  }
  .imp-ultima {
    font-size: 11px;
    color: $tinta-3;
  }
}
.fila-badge {
  font-size: 12px;
  border-radius: 12px;
  padding: 2px 10px;
  white-space: nowrap;
  &.good {
    background: rgba(12, 163, 12, 0.1);
    color: #006300;
  }
  &.warning {
    background: rgba(250, 178, 25, 0.2);
    color: #7a5200;
  }
  &.critical {
    background: rgba(208, 59, 59, 0.12);
    color: $critical;
    font-weight: 600;
  }
}
.desligados {
  font-size: 12px;
  color: $tinta-2;
  margin-bottom: 12px;
}

// 3. linha do tempo
.bloco {
  background: $superficie;
  border: 1px solid $borda;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.bloco-topo {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.bloco-titulo {
  font-weight: 600;
}
.bloco-sub {
  font-size: 12px;
  color: $tinta-2;
}
.legenda {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: $tinta-2;
  align-items: center;
  > span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}
.ponto {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px $superficie;
  background: $tinta-3;
  &.good { background: $good; }
  &.warning { background: $warning; }
  &.critical { background: $critical; }
}
.bloco-travou {
  display: inline-block;
  width: 14px;
  height: 10px;
  background: rgba(208, 59, 59, 0.18);
  border-radius: 2px;
}
.marca-queda {
  display: inline-block;
  width: 2px;
  height: 12px;
  background: $serious;
}

$largura-nome: 170px;
.pista {
  display: flex;
  align-items: stretch;
  margin-bottom: 6px;
}
.pista-nome {
  width: $largura-nome;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  padding-right: 8px;
  align-self: center;
  .pista-resumo {
    font-weight: normal;
    color: $tinta-3;
  }
}
.pista-area {
  position: relative;
  flex-grow: 1;
  height: 72px;
  margin-top: 10px;
  margin-right: 34px;
  border-bottom: 1px solid #c3c2b7;
  background: linear-gradient(to top, rgba(12, 163, 12, 0.04) 16.67%, transparent 16.67%);
}
.guia {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px solid $grade;
  span {
    position: absolute;
    left: 100%;
    margin-left: 4px;
    top: -7px;
    font-size: 10px;
    color: $tinta-3;
    white-space: nowrap;
  }
}
.travou {
  position: absolute;
  top: -10px;
  bottom: 0;
  background: rgba(208, 59, 59, 0.16);
  border-radius: 3px;
  font-size: 10px;
  color: $critical;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  overflow: visible;
  span {
    position: relative;
    top: 1px;
  }
}
.queda {
  position: absolute;
  top: -10px;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: $serious;
  i {
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: $serious;
    background: $superficie;
    padding: 1px;
  }
}
// Area de toque maior que o ponto (24px).
.alvo {
  position: absolute;
  width: 24px;
  height: 24px;
  margin-left: -12px;
  margin-bottom: -12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  &:hover .ponto {
    transform: scale(1.5);
  }
}
.eixo {
  display: flex;
  padding-left: $largura-nome;
  padding-right: 34px;
  .eixo-area {
    position: relative;
    flex-grow: 1;
    height: 18px;
    span {
      position: absolute;
      transform: translateX(-50%);
      font-size: 11px;
      color: $tinta-3;
      top: 2px;
      font-variant-numeric: tabular-nums;
    }
    .agora {
      transform: translateX(-100%);
      font-weight: 600;
      color: $tinta-2;
    }
  }
}

// 4. fila e ocorrencias
.fila-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  padding: 5px 0;
  border-top: 1px solid $grade;
  font-size: 13px;
  .fila-hora {
    width: 64px;
    color: $tinta-2;
    font-variant-numeric: tabular-nums;
  }
  .fila-o-que {
    min-width: 170px;
  }
}
.chip {
  display: inline-block;
  border-radius: 12px;
  padding: 1px 8px;
  margin: 1px 4px 1px 0;
  font-size: 12px;
  white-space: nowrap;
  &.good {
    background: rgba(12, 163, 12, 0.1);
    color: #006300;
  }
  &.warning {
    background: rgba(250, 178, 25, 0.2);
    color: #7a5200;
  }
  &.critical {
    background: rgba(208, 59, 59, 0.12);
    color: $critical;
  }
  &.cinza {
    background: #f0efec;
    color: $tinta-3;
  }
}
.ocorrencia {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  border-top: 1px solid $grade;
  font-size: 13px;
  .ocorrencia-icone {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &.critical {
      background: rgba(208, 59, 59, 0.12);
      color: $critical;
    }
    &.serious {
      background: rgba(236, 131, 90, 0.15);
      color: $serious;
    }
  }
}
@media (max-width: 768px) {
  $nome-mobile: 90px;
  .pista {
    flex-direction: column;
  }
  .pista-nome {
    width: auto;
  }
  .eixo {
    padding-left: 0;
  }
}
</style>
